import { Appliance } from "./appliance";
import { ApplianceCategory } from "./appliance-category";

type CombinedData = {
    id: string | number;
    name?: string;
    power?: number;
    iconUrl?: string;
    applianceCategoryId?: string;
    applianceCategory?: string;
    deleted?: boolean;
    imageUrl?: string;
    description?: string;
    appliances?: Array<Appliance>;
};

// Combined model with optional properties
export class CombinedAppliance {
    // We'll store id as string internally and convert as needed
    id: string;
    name: string;
    
    // Optional properties from Appliance
    power?: number;
    iconUrl?: string;
    applianceCategoryId?: string;
    applianceCategory?: string;

    // Optional properties from ApplianceCategory
    deleted?: boolean;
    imageUrl?: string;
    description?: string;
    appliances?: Array<Appliance>;

    // Internal flag to track the source type
    private _isFromCategory: boolean;

    constructor(data: CombinedData) {
        this._isFromCategory = this.determineIfCategory(data);
        
        // Convert id to string, handling both number and string cases
        this.id = data.id.toString();
        this.name = data.name || '';

        // Optional properties from Appliance
        if (data.power !== undefined) this.power = data.power;
        if (data.iconUrl) this.iconUrl = data.iconUrl;
        if (data.applianceCategoryId) this.applianceCategoryId = data.applianceCategoryId;
        if (data.applianceCategory) this.applianceCategory = data.applianceCategory;

        // Optional properties from ApplianceCategory
        if (data.deleted !== undefined) this.deleted = data.deleted;
        if (data.imageUrl) this.imageUrl = data.imageUrl;
        if (data.description) this.description = data.description;
        if (data.appliances) this.appliances = data.appliances;
    }

    private determineIfCategory(data: CombinedData): boolean {
        return !!(
            data.deleted !== undefined ||
            data.appliances !== undefined ||
            (data.imageUrl !== undefined && data.iconUrl === undefined) ||
            typeof data.id === 'string'
        );
    }

    // Method to check if instance was created from a category
    isCategory(): boolean {
        return this._isFromCategory;
    }

    // Helper method to get ID as number (for Appliance)
    getNumericId(): number {
        const numId = parseInt(this.id);
        if (isNaN(numId)) {
            throw new Error('ID cannot be converted to a number');
        }
        return numId;
    }

    // Helper method to convert to basic Appliance
    toAppliance(): Appliance {
        if (this.isCategory()) {
            throw new Error('Cannot convert category to appliance');
        }
        return new Appliance({
            id: this.getNumericId(),
            name: this.name,
            power: this.power || 0,
            iconUrl: this.iconUrl || '',
            applianceCategoryId: this.applianceCategoryId || '',
            applianceCategory: this.applianceCategory || ''
        });
    }

    // Helper method to convert to ApplianceCategory
    toApplianceCategory(): ApplianceCategory {
        if (!this.isCategory()) {
            throw new Error('Cannot convert appliance to category');
        }
        return {
            id: this.id,
            name: this.name,
            deleted: this.deleted || true,
            imageUrl: this.imageUrl || '',
            power: 0,
            description: this.description || '',
            applianceCategoryId: this.applianceCategoryId || '',
            applianceCategory: this.applianceCategory || '',
            appliances: this.appliances || []
        };
    }
}



